import React, { useState } from 'react';

import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Divider,
} from '@mui/material';

import ViewGroupModal from '../ViewGroupModal';
import MemberTable from './MemberTable';
import Button from 'components/Button';
import { useToast } from 'hooks/useToast';
import { Group } from 'pages/DigitalGovernance/GovernanceThematicGroups/types/groups';
import { apiOrchestrator } from 'services/apiService';

const GroupAccordion: React.FC<{ group: Group; onUpdate: () => void }> = ({ group, onUpdate }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [viewGroupModalOpen, setViewGroupModalOpen] = useState(false);
  const { toastSuccess, toastError } = useToast();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenConfirm = () => {
    setConfirmOpen(true);
    handleMenuClose();
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
  };

  const handleDeleteGroup = async () => {
    try {
      await apiOrchestrator.delete(`/team/${group.uuid}`);
      toastSuccess('Grupo excluído com sucesso!');
      setConfirmOpen(false);
      onUpdate();
    } catch (error) {
      console.error('Erro ao excluir grupo:', error);
      toastError('Erro ao excluir grupo.');
    }
  };

  const handleViewGroup = () => {
    setViewGroupModalOpen(true);
    handleMenuClose();
  };

  const handleCloseGroupModal = () => {
    onUpdate();
    setViewGroupModalOpen(false);
  };

  return (
    <>
      <Accordion
        sx={{
          width: '100%',
          marginBottom: 1,
          background: 'background.secondary',
        }}
      >
        <AccordionSummary
          sx={{
            '& .MuiAccordionSummary-expandIconWrapper': {
              pointerEvents: 'auto',
            },
            '& .MuiAccordionSummary-root': {
              minHeight: '3rem',
            },
            '& .MuiAccordionSummary-content': {
              margin: 0,
            },
            '& .MuiAccordionDetails-root': {
              padding: '0 1rem',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              justifyContent: 'space-between',
              paddingRight: '2.5rem',
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                display: 'flex',
                flex: '0.33',
              }}
            >
              {group.name}
            </Typography>
            <Typography
              variant="subtitle1"
              textAlign="left"
              sx={{
                width: '100%',
                flex: '0.33',
              }}
            >
              {group.members.length}
            </Typography>
            <IconButton onClick={handleMenuOpen}>
              <MoreHorizIcon sx={{ color: 'primary' }} />
            </IconButton>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <MemberTable members={group.members} />
        </AccordionDetails>
      </Accordion>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: 'background.primary',
            color: 'primary',
            borderRadius: '8px',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            padding: '2px',
          },
          '& .MuiMenuItem-root': {
            fontSize: '0.75rem',
            padding: '2px 4px',
            '&:hover': {
              backgroundColor: 'primary',
              color: 'black',
              borderRadius: '4px',
            },
          },
        }}
      >
        <MenuItem onClick={handleViewGroup}>Visualizar grupo</MenuItem>
        <MenuItem onClick={handleOpenConfirm}>Excluir grupo</MenuItem>
      </Menu>

      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        PaperProps={{
          sx: {
            background: 'background.secondary',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            width: '400px',
            maxWidth: '100%',
            margin: '0 auto',
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            color: 'text.primary',
            fontWeight: 'bold',
            fontSize: 18,
          }}
        >
          CONFIRMAÇÃO
        </DialogTitle>

        <Divider />

        <DialogContent>
          <Typography
            sx={{
              color: 'text.primary',
              textAlign: 'center',
              mb: 2,
              fontSize: '1rem',
            }}
          >
            Deseja realmente excluir o grupo temático{' '}
            <Typography component="span" sx={{ fontWeight: 'bold' }}>
              {group.name}
            </Typography>
            ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button onClick={handleCloseConfirm} variant="outline" fullWidth>
            Cancelar
          </Button>
          <Button onClick={handleDeleteGroup} variant="red" fullWidth>
            Excluir Grupo Temático
          </Button>
        </DialogActions>
      </Dialog>

      <ViewGroupModal
        open={viewGroupModalOpen}
        onClose={handleCloseGroupModal}
        groupId={group.uuid}
        groupName={group.name}
        initialData={group}
        onUpdate={onUpdate}
      />
    </>
  );
};

export default GroupAccordion;
